import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer } from 'recharts';
import html2pdf from 'html2pdf.js';
import { DataManager } from '../../services/dataManager';

const CustomerReportTemplate = ({ client, initialContractId = null, onClose }) => {
    const clientContracts = useMemo(() => DataManager.getClientContracts(client), [client]);
    const [selectedContractId, setSelectedContractId] = useState(
        initialContractId || (clientContracts.length > 0 ? (clientContracts.find(c => c.status === 'attivo')?.id || clientContracts[0].id) : 'all')
    );

    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const reportRef = useRef(null);

    const activeContract = useMemo(() => {
        return clientContracts.find(c => c.id === selectedContractId) || (client.contract?.title ? client.contract : null);
    }, [clientContracts, selectedContractId, client.contract]);

    const handleDownloadPDF = useCallback(async () => {
        if (!reportRef.current || downloading) return;
        setDownloading(true);
        try {
            const element = reportRef.current;
            const filename = `AwarenessTool_${client.name.replace(/\s+/g, '_')}_Report_${new Date().toISOString().split('T')[0]}.pdf`;
            const opt = {
                margin: 0,
                filename: filename,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, letterRendering: true, width: 794 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: { mode: ['css', 'legacy'], before: '.page-break' }
            };
            await html2pdf().set(opt).from(element).save();
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setDownloading(false);
        }
    }, [client.name, downloading]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [baselines, allMeetings, works, questionnaireData] = await Promise.all([
                    DataManager.getAllBaselines(client.id),
                    DataManager.getClientMeetings(client.id, false),
                    DataManager.getClientWorks(client.id, true),
                    DataManager.getQuestionnaireAnswers(client.id)
                ]);

                // Filter meetings if specific contract selected
                let meetings = allMeetings;
                if (selectedContractId && selectedContractId !== 'all') {
                    const filtered = allMeetings.filter(m => m.contractId === selectedContractId);
                    if (filtered.length > 0) {
                        meetings = filtered;
                    }
                }

                let counselorName = "Tua Counselor";
                if (client.counselorId) {
                    const counselor = await DataManager.getCounselorByUid(client.counselorId);
                    if (counselor && counselor.name) counselorName = counselor.name;
                }

                const validBaselines = baselines
                    .filter(b => b.wheelOfLife && Object.keys(b.wheelOfLife).length > 0)
                    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

                const T0 = validBaselines[0] || null;
                const T2 = validBaselines[validBaselines.length - 1] || null;

                // LOGICA STRUMENTI: Estrae in modo puntuale le tecniche selezionate negli incontri
                const toolsFound = [];
                const toolIdToLabel = {
                    'respirazione': 'Respirazione',
                    'carte_creative': 'Carte Creative',
                    'frequenze': 'Frequenze',
                    'sogno': 'Lavoro Onirico',
                    'sedia_vuota': 'Sedia Vuota',
                    'baseline': 'Test Baseline',
                    'body_scan': 'Grounding / Body Scan',
                    'scrittura': 'Scrittura'
                };

                meetings.forEach(m => {
                    const exercises = m.observations?.exercises || m.exercises || [];
                    exercises.forEach(ex => {
                        toolsFound.push(toolIdToLabel[ex] || ex); // Use label if known, else the raw string
                    });

                    // Legacy support per vecchi dati stringa
                    if (m.observations?.techniques && typeof m.observations.techniques === 'string') {
                        const techs = m.observations.techniques.split(',').map(t => t.trim()).filter(Boolean);
                        toolsFound.push(...techs);
                    }
                });

                if (works.some(w => w.type?.toLowerCase().includes('breath') || w.title?.toLowerCase().includes('respiro'))) {
                    toolsFound.push('Respirazione');
                }
                
                const keywords = await DataManager.analyzeClientTexts(works, meetings, 30);
                const themes = DataManager.summarizeThemes(keywords, 8);
                const narrative = DataManager.generateCustomerReportSummary(works, meetings, client.name, questionnaireData?.answers || {}, counselorName);

                setReportData({ T0, T2, keywords, themes, narrative, tools: [...new Set(toolsFound)], counselorName });
            } catch (error) {
                console.error("Error:", error);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [client.id, selectedContractId]);

    const radarData = useMemo(() => {
        if (!reportData?.T2?.wheelOfLife) return [];

        const dbKeys = Object.keys(reportData.T2.wheelOfLife);

        return dbKeys.map(key => ({
            area: key.charAt(0).toUpperCase() + key.slice(1),
            Iniziale: reportData.T0?.wheelOfLife?.[key] || 0,
            Finale: reportData.T2.wheelOfLife[key] || 0
        }));
    }, [reportData]);

    if (loading || !reportData) return <div className="fixed inset-0 bg-slate-900 flex items-center justify-center z-50 text-white font-bold">CARICAMENTO DATI...</div>;

    return (
        <div className="fixed inset-0 bg-slate-800 overflow-y-auto z-50 pt-32 pb-10 print:p-0">
            {/* Styles for contentEditable preview and print */}
            <style dangerouslySetInnerHTML={{ __html: `
                [contenteditable="true"] {
                    transition: all 0.2s ease;
                    outline: none;
                }
                [contenteditable="true"]:hover {
                    outline: 1px dashed #6366f1 !important;
                    background-color: rgba(99, 102, 241, 0.03);
                    border-radius: 4px;
                    cursor: text;
                }
                [contenteditable="true"]:focus {
                    outline: 2px solid #4f46e5 !important;
                    background-color: rgba(79, 70, 229, 0.05) !important;
                    border-radius: 4px;
                }
                @media print {
                    [contenteditable="true"] {
                        outline: none !important;
                        background: transparent !important;
                    }
                }
            `}} />

            {/* Header Action Bar */}
            <div className="fixed top-0 left-0 right-0 bg-white p-4 flex justify-between items-center z-[60] border-b shadow-md print:hidden flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    <h2 className="font-black text-slate-800 uppercase text-sm ml-2">Report: <span className="capitalize normal-case">{client.name}</span></h2>
                    {clientContracts.length > 0 && (
                        <div className="flex items-center gap-1.5 bg-indigo-50 px-2.5 py-1 rounded-xl border border-indigo-100">
                            <span className="text-[10px] font-bold text-indigo-700 uppercase">Contratto:</span>
                            <select
                                value={selectedContractId}
                                onChange={(e) => setSelectedContractId(e.target.value)}
                                className="bg-white text-xs font-bold text-slate-800 border border-indigo-200 rounded-lg px-2 py-0.5 focus:outline-none"
                            >
                                <option value="all">Tutti gli incontri</option>
                                {clientContracts.map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.status === 'attivo' ? '🟢' : '⚪'} {c.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
                <div className="flex gap-3 mr-2">
                    <button onClick={handleDownloadPDF} disabled={downloading} className="px-5 py-2 bg-indigo-600 text-white rounded-xl font-bold shadow-md hover:bg-indigo-700 transition-all text-xs">
                        {downloading ? 'GENERAZIONE PDF...' : 'SCARICA PDF'}
                    </button>
                    <button onClick={onClose} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-300 text-xs">CHIUDI</button>
                </div>
            </div>

            {/* Info Banner for inline editing */}
            <div className="fixed top-[73px] left-0 right-0 bg-indigo-600 text-white p-2 text-center text-xs font-bold z-50 shadow-sm print:hidden">
                💡 Puoi fare clic su qualsiasi testo nel report sottostante per modificarlo o correggere errori prima di scaricare il PDF.
            </div>

            <div ref={reportRef} style={{ width: '210mm', margin: '0 auto', backgroundColor: '#1e293b' }}>

                {/* PAGINA 1: IDENTITÀ E SINTESI */}
                <div className="bg-white" style={{ width: '210mm', height: '296mm', padding: '20mm', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                    <div className="text-center border-b-2 border-indigo-50 pb-6 mb-8">
                        <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">AWARENESS TOOL</h1>
                        <p className="text-indigo-600 font-bold uppercase tracking-[0.4em] text-[10px]">Report del Percorso di Consapevolezza</p>

                        <div className="flex justify-between mt-10 text-left">
                            <div>
                                <p className="text-[9px] font-black text-slate-400 uppercase">Professionista</p>
                                <p className="text-sm font-bold text-slate-800" contentEditable={true} suppressContentEditableWarning={true}>{reportData.counselorName}</p>
                            </div>
                            {activeContract?.title && (
                                <div className="text-center mx-4 flex-1">
                                    <p className="text-[9px] font-black text-slate-400 uppercase">Focus del Percorso</p>
                                    <p className="text-sm font-bold text-slate-800 italic" contentEditable={true} suppressContentEditableWarning={true}>"{activeContract.title.charAt(0).toUpperCase() + activeContract.title.slice(1)}"</p>
                                </div>
                            )}
                            <div className="text-right">
                                <p className="text-[9px] font-black text-slate-400 uppercase">Cliente</p>
                                <p className="text-lg font-black text-slate-900 capitalize" contentEditable={true} suppressContentEditableWarning={true}>{client.name}</p>
                                <p className="text-[10px] text-slate-500 font-bold">{new Date().toLocaleDateString('it-IT')}</p>
                            </div>
                        </div>
                    </div>

                    <section className="mb-8">
                        <h2 className="text-sm font-black uppercase text-slate-900 mb-4">1. Mappa Semantica</h2>
                        <div className="bg-slate-50 p-6 rounded-[2rem] border border-slate-100 flex flex-wrap gap-2 justify-center">
                            {Object.entries(reportData.keywords).slice(0, 22).map(([word, data], idx) => {
                                const displayWord = data.display || word;
                                const capitalizedWord = displayWord ? displayWord.charAt(0).toUpperCase() + displayWord.slice(1) : '';
                                return (
                                    <span key={idx} contentEditable={true} suppressContentEditableWarning={true} className={`px-3 py-1.5 rounded-full font-bold text-[10px] border ${data.category === 'theme' ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-200 text-slate-500'}`}>
                                        {capitalizedWord}
                                    </span>
                                );
                            })}
                        </div>
                    </section>

                    <section className="flex-1">
                        <h2 className="text-sm font-black uppercase text-slate-900 mb-4">2. Sintesi del Percorso</h2>
                        <div className="bg-indigo-50/30 p-8 rounded-[2rem] border border-indigo-100 h-full flex flex-col gap-4">
                            <p className="text-indigo-900 leading-relaxed italic text-[13px]" contentEditable={true} suppressContentEditableWarning={true}>{reportData.narrative.intro}</p>
                            <ul className="space-y-3 text-indigo-900 leading-relaxed text-[13px]">
                                {reportData.narrative.bullets.map((b, i) => (
                                    <li key={i}>
                                        <span className="mr-2 text-indigo-400">•</span>
                                        {b.label && <strong contentEditable={true} suppressContentEditableWarning={true} className="inline-block mr-1">{b.label}</strong>} <span contentEditable={true} suppressContentEditableWarning={true} className="italic">{b.text}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </section>
                </div>

                <div className="page-break" style={{ height: '1px' }}></div>

                {/* PAGINA 2: RUOTA DINAMICA E STRUMENTI */}
                <div className="bg-white" style={{ width: '210mm', height: '296mm', padding: '20mm', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                    <section className="mb-10 pt-4">
                        <h2 className="text-sm font-black uppercase text-slate-900 mb-6">3. Evoluzione della Ruota della Vita</h2>
                        <div className="bg-slate-50 p-6 rounded-[2.5rem] border border-slate-100">
                            <ResponsiveContainer width="100%" height={400}>
                                <RadarChart data={radarData}>
                                    <PolarGrid stroke="#cbd5e1" />
                                    <PolarAngleAxis dataKey="area" tick={{ fill: '#475569', fontSize: 11, fontWeight: '900' }} />
                                    <PolarRadiusAxis domain={[0, 10]} tick={false} />
                                    <Radar name="Partenza" dataKey="Iniziale" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.2} strokeWidth={2} />
                                    <Radar name="Stato Attuale" dataKey="Finale" stroke="#4f46e5" fill="#4f46e5" fillOpacity={0.4} strokeWidth={4} />
                                    <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                </RadarChart>
                            </ResponsiveContainer>
                        </div>
                    </section>

                    <section className="grid grid-cols-2 gap-12 mt-4 flex-1">
                        <div>
                            <h2 className="text-[11px] font-black uppercase text-slate-900 mb-2 tracking-widest border-b pb-2 border-slate-100">Temi di Consapevolezza</h2>
                            <p className="text-[10px] text-slate-500 mb-4 italic">Nel corso del percorso sono emerse delle aree ancora da esplorare</p>
                            <ul className="space-y-4">
                                {reportData.themes.map((theme, i) => {
                                    const capitalizedTheme = theme.word ? theme.word.charAt(0).toUpperCase() + theme.word.slice(1) : '';
                                    return (
                                        <li key={i} className="flex items-center gap-3 text-xs font-bold text-slate-600">
                                            <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full flex-shrink-0"></div> <span contentEditable={true} suppressContentEditableWarning={true}>{capitalizedTheme}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                        <div>
                            <h2 className="text-[11px] font-black uppercase text-slate-900 mb-6 tracking-widest border-b pb-2 border-slate-100">Strumenti Utilizzati</h2>
                            <div className="flex flex-wrap gap-2">
                                {reportData.tools.length > 0 ? reportData.tools.map((tool, i) => (
                                    <span key={i} contentEditable={true} suppressContentEditableWarning={true} className="px-3 py-1.5 bg-slate-900 text-white rounded-xl text-[10px] uppercase font-black tracking-tight outline-none">
                                        {tool}
                                    </span>
                                )) : <span className="text-xs italic text-slate-400" contentEditable={true} suppressContentEditableWarning={true}>Colloqui di counseling e supporto empatico.</span>}
                            </div>
                        </div>
                    </section>

                    <div className="mt-auto pt-8 border-t-2 border-slate-50 flex justify-between items-end">
                        <p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.2em]">AwarenessTool</p>
                        <div className="text-right">
                            <div className="w-40 h-px bg-slate-200 mb-2 ml-auto"></div>
                            <p className="text-[10px] font-black text-slate-400 uppercase italic" contentEditable={true} suppressContentEditableWarning={true}>Firma del Professionista</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerReportTemplate;